import React, {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useConnection } from '../connection/connection';
import { Spin } from 'antd';
import { useEventListener } from '../common/EventContext';
import { Vendor } from '../dto/Vendor';
import { Warehouse } from '../dto/Warehouse';
import { IncomeTypes, State, User } from '../dto';
import { DayJs } from '../common/Utils';
import { Mutex } from '../common/Mutex';

export class Site {
  businessId: string;
  siteId: string;
  name: string;
  description?: string;
}

export class Currency {
  currencyCode: string;
  currencyName: string;
}

export class CurrencyRate {
  currencyPair: string;
  rate: number;
}

export class Account {
  businessId: string;
  accountId: string;
  displayId: string;
  accountCategory: string;
  accountTypeId: string;
  accountTypeName: string;
  activity: boolean;
  name: string;
  siteId?: string;
  description?: string;
  currency: string;
  balance: number;
  sortOrder: number;
  _version: number;
}

export class AccountType {
  businessId: string;
  accountTypeId: string;
  accountCategory: string;
  name: string;
  description?: string;
  multiCurrency: boolean;
  allowAdd: boolean;
  activity: boolean;
  sortOrder: number;
}

export type BusinessContextData = {
  businessId: string;
  currency: string;
  country: string;
  sites: Record<string, Site>;
  users: Record<string, User>;
  accounts: Record<string, Account>;
  accountTypes: Record<string, AccountType>;
  expenseTypes: Record<string, { key: string; value: string }>;
  incomeTypes: Record<string, IncomeTypes>;
  states: Record<string, State>;
  select: Record<string, { label: string; value: string; options?: any[] }[]>;
  currencies: Record<string, Currency>;
  getAccountsForSelect(
    filter?: (account: Account) => boolean,
  ): { label: string; value: string; options?: any[] }[];
  getSelectValuesAsync(
    type: string,
  ): Promise<{ label: string; value: string; options?: any[] }[]>;
  getCurrencyRate(
    sourceCcy: string,
    targetCcry: string,
    date?: Date,
  ): Promise<CurrencyRate>;
  getCachedCurrencyRate(
    sourceCcy: string,
    targetCcry: string,
    date?: Date,
  ): CurrencyRate;
  getAccountsInSite(siteId?: string): Account[];
};
export type TxType = 'transfer' | 'invoice' | 'bill' | 'journal' | 'intersol';
export class AccountTransaction {
  businessId: string;
  accountId: string;
  siteId: string;
  txId: number;
  date: string;
  timestamp: number;
  created: number;
  description: string;
  activity: boolean;
  txType: TxType;
  otherSide: string;
  amount: number;
  userId: string;
  createdUserId: string;
  currency?: string;
  fcDetails?: FcDetails;
  _version: number;
}

export class CashActivityItem {
  accountId: string;
  siteId: string;
  description: string;
  amount: number;
  txType: TxType;
  otherSide: string;
  fcDetails?: FcDetails;
}

export class CashActivity {
  businessId: string;
  date: string;
  type: 'deposit' | 'withdraw';
  userId: string;
  notes?: string;
  category: string;
  items: CashActivityItem[];
}

export class Journal {
  hk: string; // businessId:txId
  businessId: string;
  businessRk: string; // date:timestamp:txId
  txId: number;
  date: string;
  timestamp: number;
  description: string;
  notes: string;
  amount: number;
  userId: string;
  items: AccountTransaction[];
  _version: number;
}

export type FcDetails = {
  accountCcy: string;
  receivedCcy: string;
  exchangeRate: number;
  accountCcyAmount: number;
  receivedCcyAmount: number;
};

export const BusinessContext = React.createContext<BusinessContextData>(
  {} as any,
);

const dummy = async () => {};

export const BusinessContextProvider: FC<PropsWithChildren> = (props) => {
  const [businessId, setBusinessId] = useState<string>(
    '4359286a-b7d9-43f0-8e9c-b89ccfea6080',
  );
  const [sites, setSites] = useState<Record<string, Site>>({});
  const [users, setUsers] = useState<Record<string, User>>({});
  const [accounts, setAccounts] = useState<Record<string, Account>>({});
  const [accountTypes, setAccountTypes] = useState<Record<string, AccountType>>(
    {},
  );
  const [expenseTypes, setExpenseTypes] = useState<
    Record<string, { key: string; value: string }>
  >({});
  const [states, setStates] = useState<Record<string, State>>({});
  const [select, setSelect] = useState<
    Record<string, { label: string; value: string | number; options?: any[] }[]>
  >({ sites: [], accounts: [], accountTypes: [] });
  const [currencies, setCurrencies] = useState<Record<string, Currency>>({});
  // const [currencyRates, setCurrencyRates] = useState<
  //   Record<string, Record<string, CurrencyRate>>
  // >({});

  const currencyRates = useRef<Record<string, Record<string, CurrencyRate>>>(
    {},
  );

  const [loading, setLoading] = useState<boolean>(true);
  const [incomeTypes, setIncomeTypes] = useState<Record<string, IncomeTypes>>(
    {},
  );
  const connection = useConnection();

  const mutex = useRef<Mutex>(new Mutex());

  const fetchCurrencyRatesForDate = useCallback(
    async (date?: Date) => {
      const release = await mutex.current.lock();
      try {
        const requestedDate = DayJs(date).format('YYYY-MM-DD');
        let existingRates = currencyRates.current[requestedDate];
        if (!existingRates) {
          const url = !date
            ? `fx/rates/list`
            : `fx/rates/list/${requestedDate}`;
          const res = await connection.get(url);
          existingRates = res.data
            ? (Object.fromEntries(
                res.data.map((item: CurrencyRate) => [item.currencyPair, item]),
              ) as Record<string, CurrencyRate>)
            : {};
          currencyRates.current[requestedDate] = existingRates;
        }
        return existingRates;
      } finally {
        release();
      }
    },
    [connection],
  );

  useEffect(() => {
    setLoading(true);
    const businessContextPromise = connection
      .get(`businesses/${businessId}/context`)
      .then((res) => {
        setAccounts(
          Object.fromEntries(
            res.data.accounts.map((item: Account) => [item.accountId, item]),
          ),
        );
        setStates(
          Object.fromEntries(
            res.data.states.map((item: State) => [item.id, item]),
          ),
        );
        setAccountTypes(
          Object.fromEntries(
            res.data.accountTypes.map((item: AccountType) => [
              item.accountTypeId,
              item,
            ]),
          ),
        );
        setUsers(
          Object.fromEntries(
            res.data.users.map((item: User) => [item.userId, item]),
          ),
        );
        setSites(
          Object.fromEntries(
            res.data.sites.map((item: Site) => [item.siteId, item]),
          ),
        );
        setExpenseTypes(
          Object.fromEntries(
            res.data.expenseTypes.map(
              (item: { key: string; value: string }) => [item.key, item],
            ),
          ),
        );
        setIncomeTypes(
          Object.fromEntries(
            res.data.incomeTypes.map((item: IncomeTypes) => [item.key, item]),
          ),
        );
        setSelect({
          sites: res.data.sites.map((item: Site) => ({
            label: item.name,
            value: item.siteId,
          })),
          accounts: res.data.accountTypes.map((item: AccountType) => ({
            label: item.name,
            options: res.data.accounts
              .filter(
                (acc: Account) => acc.accountTypeId === item.accountTypeId,
              )
              .map((acc: Account) => ({
                label: acc.name,
                value: acc.accountId,
              })),
          })),
          accountTypes: res.data.accountTypes.map((item: AccountType) => ({
            label: item.name,
            value: item.accountTypeId,
          })),
          expenseTypes: res.data.expenseTypes.map(
            (item: { key: string; value: string }) => ({
              label: item.value,
              value: item.key,
              name: item.value,
            }),
          ),
          incomeTypes: res.data.incomeTypes.map((item: IncomeTypes) => ({
            label: item.name,
            value: item.key,
          })),
          states: res.data.states.map((item: State) => ({
            name: item.name,
            value: item.id,
            label: item.name,
          })),
          incomeStatus: [
            'Pending Approval',
            'Approved',
            'Cancelled',
            'Rejected',
          ].map((item) => ({
            label: item,
            value: item,
          })),
        });
      });

    const currencyListPromise = connection
      .get(`fx/currency/list`)
      .then((res) => {
        setCurrencies(
          Object.fromEntries(
            res.data.map((item: Currency) => [item.currencyCode, item]),
          ),
        );
      });

    // const ratesListPromise = fetchCurrencyRatesForDate();

    Promise.all([
      businessContextPromise,
      currencyListPromise,
      // ratesListPromise,
    ]).finally(() => setLoading(false));
  }, [businessId, connection]);

  const getCurrencyRate = useCallback(
    async (
      sourceCcy: string,
      targetCcry: string,
      date?: Date,
    ): Promise<CurrencyRate> => {
      const rates = await fetchCurrencyRatesForDate(date);
      if (!rates) {
        throw new Error('Currency rates not found');
      }
      return rates[`${sourceCcy}/${targetCcry}`];
    },
    [fetchCurrencyRatesForDate],
  );

  const getCachedCurrencyRate = useCallback(
    (sourceCcy: string, targetCcry: string, date?: Date): CurrencyRate => {
      const requestedDate = DayJs(date).format('YYYY-MM-DD');
      const existingRates = currencyRates.current[requestedDate];
      return existingRates[`${sourceCcy}/${targetCcry}`];
    },
    [],
  );

  // const getCachedCurrencyRate = useCallback(
  //   (
  //     sourceCcy: string,
  //     targetCcry: string,
  //     date?: Date,
  //   ): CurrencyRate | undefined => {
  //     const requestedDate = DayJs(date).format('YYYY-MM-DD');
  //     return currencyRates[requestedDate]?.[`${sourceCcy}/${targetCcry}`];
  //   },
  //   [currencyRates],
  // );

  const getAccountsForSelect = useCallback(
    (filter?: (account: Account) => boolean) => {
      return Object.values(accountTypes)
        .map((accType) => ({
          label: accType.name,
          value: `acctype:${accType.accountTypeId}`,
          options: Object.values(accounts)
            .filter((acc) => acc.accountTypeId === accType.accountTypeId)
            .filter((acc) => (filter ? filter(acc) : true))
            .map((acc) => ({ label: acc.name, value: acc.accountId })),
        }))
        .filter((value) => value.options.length > 0);
    },
    [accountTypes, accounts],
  );

  const getSelectValuesAsync = useCallback(
    async (type: string) => {
      const values = select[type];
      if (values !== undefined) {
        return values;
      }
      try {
        const res = await connection.get(`${businessId}/${type}/for/enums`);
        setSelect({ ...select, [type]: res.data });
        return res.data;
      } catch (e) {
        return [];
      }
    },
    [businessId, connection, select],
  );

  const getAccountsInSite = useCallback(
    (siteId?: string) => {
      return Object.values(accounts).filter(
        (acc) =>
          acc.siteId === siteId || !siteId || acc.siteId === '0' || !acc.siteId,
      );
    },
    [accounts],
  );

  useEventListener(
    'VENDOR',
    useCallback((dt) => {
      setSelect((prevState: any) => {
        if (prevState['vendors'] === undefined) {
          return prevState;
        }
        const item: Vendor = dt.data;
        if (dt.action === 'CREATE') {
          return {
            ...prevState,
            vendors: [
              ...prevState['vendors'],
              { label: item.name, value: item.vendorId },
            ],
          };
        } else if (dt.action === 'UPDATE') {
          return {
            ...prevState,
            vendors: [
              prevState['vendors'].map((v: any) =>
                v.value === item.vendorId
                  ? { label: item.name, value: item.vendorId }
                  : v,
              ),
            ],
          };
        }
      });
    }, []),
  );

  useEventListener(
    'WAREHOUSE',
    useCallback((dt) => {
      setSelect((prevState: any) => {
        if (prevState['warehouses'] === undefined) {
          return prevState;
        }
        const item: Warehouse = dt.data;
        if (dt.action === 'CREATE') {
          return {
            ...prevState,
            vendors: [
              ...prevState['vendors'],
              {
                label: item.name,
                value: item.warehouseId,
                siteId: item.siteId,
              },
            ],
          };
        } else if (dt.action === 'UPDATE') {
          return {
            ...prevState,
            vendors: [
              prevState['vendors'].map((v: any) =>
                v.value === item.warehouseId
                  ? {
                      label: item.name,
                      value: item.warehouseId,
                      siteId: item.siteId,
                    }
                  : v,
              ),
            ],
          };
        }
      });
    }, []),
  );

  return (
    <BusinessContext.Provider
      value={{
        businessId,
        currency: 'LKR',
        country: 'LK',
        select: select as any,
        sites,
        accounts,
        users,
        accountTypes,
        expenseTypes,
        states,
        currencies,
        getAccountsForSelect,
        getSelectValuesAsync,
        getCurrencyRate,
        getCachedCurrencyRate,
        getAccountsInSite,
        incomeTypes,
      }}
    >
      {loading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            width: '100vw',
            position: 'fixed',
            top: 0,
            left: 0,
          }}
        >
          <Spin spinning={true} />
        </div>
      ) : (
        props.children
      )}
    </BusinessContext.Provider>
  );
};

export const useBusinessContext = () => useContext(BusinessContext);
